// Visitor side of talking NPCs.
//
// NpcTalkProximity (inside the Canvas) finds the nearest NPC that talks.
// NpcTalkPanel (DOM) shows "Talk to Zora" next to it, and the chat: type, or
// hold the mic to speak; the NPC answers with a caption and its voice, louder
// the closer you stand. The world owner pays for replies (talk credits); when
// they run out the NPC says it's resting.
import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import { useXR } from '@react-three/xr';
import { MessageCircle, Mic, Send, X, Loader } from 'lucide-react';
import type { PrimitiveItem } from '../../stores/worldStore';
import { useNpcTalk, npcBodies, TALK_REACH, TALK_KEEP } from './npcTalkStore';
import { useNpcConversation, useNpcCanTalk } from './useNpcConversation';
import './NpcTalk.css';

const _me = new THREE.Vector3();
const _npc = new THREE.Vector3();

/** Inside the Canvas: who's the nearest talking NPC? */
export function NpcTalkProximity({ ids, avatarRef }: { ids: string[]; avatarRef?: React.MutableRefObject<THREE.Object3D | null> }) {
  const { camera } = useThree();
  // In VR the headset is you (the desktop avatar ref is stale there).
  const presenting = useXR((s) => !!s.session);
  useFrame(() => {
    const src = presenting ? null : avatarRef?.current;
    if (src) src.getWorldPosition(_me); else camera.getWorldPosition(_me);
    let best: string | null = null;
    let bestD = Infinity;
    let talkingD = Infinity;
    const { talkingTo } = useNpcTalk.getState();
    for (const id of ids) {
      const b = npcBodies.get(id);
      if (!b) continue;
      b.getWorldPosition(_npc);
      if (Math.abs(_npc.y - _me.y) > 2.2) continue; // another floor
      const d = Math.hypot(_npc.x - _me.x, _npc.z - _me.z);
      if (id === talkingTo) talkingD = d;
      if (d < bestD) { bestD = d; best = id; }
    }
    const st = useNpcTalk.getState();
    if (talkingTo && talkingD > TALK_KEEP) st.setTalkingTo(null);
    st.setNear(bestD <= TALK_REACH ? best : null, bestD);
  });
  return null;
}

/** DOM: prompt + chat for the NPC you're next to. */
export function NpcTalkPanel({ worldId, primitives }: { worldId: string; primitives: Record<string, PrimitiveItem> }) {
  const near = useNpcTalk((s) => s.near);
  const talkingTo = useNpcTalk((s) => s.talkingTo);
  const setTalkingTo = useNpcTalk((s) => s.setTalkingTo);
  const id = talkingTo ?? near;
  const npc = id ? primitives[id]?.npc : undefined;

  const canTalk = useNpcCanTalk(worldId, near);

  // T opens the chat when you're next to a talking NPC.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.key.toLowerCase() === 't' && near && !talkingTo && canTalk) {
        e.preventDefault();
        if (document.pointerLockElement) document.exitPointerLock();
        setTalkingTo(near);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [near, talkingTo, canTalk, setTalkingTo]);

  if (!id || !npc?.talk?.enabled) return null;
  if (!talkingTo) {
    if (!canTalk) return null;
    return (
      <button className="npc-chat-prompt" onClick={() => { if (document.pointerLockElement) document.exitPointerLock(); setTalkingTo(id); }}>
        <MessageCircle size={16} /> Talk to {npc.name} <kbd>T</kbd>
      </button>
    );
  }
  return <NpcChat key={talkingTo} worldId={worldId} npcId={talkingTo} name={npc.name} hasGreeting={!!npc.talk.greeting} onClose={() => setTalkingTo(null)} />;
}

function NpcChat({ worldId, npcId, name, hasGreeting, onClose }: { worldId: string; npcId: string; name: string; hasGreeting: boolean; onClose: () => void }) {
  const [text, setText] = useState('');
  const logRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { lines, busy, resting, send, startRec, stopRec, recording } = useNpcConversation(worldId, npcId, hasGreeting, () => inputRef.current?.focus());

  useEffect(() => { logRef.current?.scrollTo({ top: logRef.current.scrollHeight }); }, [lines, busy]);

  return (
    <div className="npc-chat" role="dialog" aria-label={`Talking with ${name}`}
      onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Escape') onClose(); }} onKeyUp={(e) => e.stopPropagation()}>
      <div className="npc-chat-head">
        <div><b>{name}</b><small>{resting ? 'Resting' : busy === 'thinking' ? 'Thinking…' : 'Talk, or hold the mic to speak'}</small></div>
        <button onClick={onClose} aria-label="Close"><X size={18} /></button>
      </div>
      <div className="npc-chat-log" ref={logRef}>
        {lines.length === 0 && !busy && <div className="npc-chat-line note">Say hi to {name}.</div>}
        {lines.map((l, i) => (
          <div key={i} className={`npc-chat-line ${l.role === 'user' ? 'me' : l.role === 'assistant' ? 'npc' : 'note'}`}>{l.content}</div>
        ))}
        {busy === 'thinking' && <div className="npc-chat-line note"><Loader size={13} className="npc-chat-spin" /> {name} is thinking…</div>}
        {busy === 'listening' && <div className="npc-chat-line note"><Loader size={13} className="npc-chat-spin" /> Listening…</div>}
      </div>
      <form className="npc-chat-form" onSubmit={(e) => { e.preventDefault(); const t = text; setText(''); void send(t); }}>
        <input ref={inputRef} value={text} maxLength={400} disabled={resting} placeholder={resting ? `${name} is resting` : `Say something to ${name}…`} onChange={(e) => setText(e.target.value)} />
        <button
          type="button"
          className={recording ? 'rec' : ''}
          disabled={resting || (!!busy && !recording)}
          title="Hold to talk"
          onPointerDown={(e) => { e.preventDefault(); void startRec(); }}
          onPointerUp={() => void stopRec()}
          onPointerLeave={() => { if (recording) void stopRec(); }}
        ><Mic size={17} /></button>
        <button type="submit" className="send" disabled={!text.trim() || !!busy || resting} aria-label="Send"><Send size={16} /></button>
      </form>
    </div>
  );
}

export default NpcTalkPanel;
